import { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { RoleEditor } from '../RoleEditor/RoleEditor.tsx';
import { Loading, Section } from '../../shared';

import {
    useCmsUser,
    useCmsUserSearch,
    useUserRoleCatalog,
} from '../../hooks/users.ts';
import { useUserProfile } from '../../hooks/useUserProfile.ts';

import type { CmsUserSummary } from '../../types/userManagementTypes';

import styles from './UserManagement.module.scss';

const SEARCH_LABEL = 'Search user by email or name';

const AUTOCOMPLETE_SLOT_PROPS = {
    paper: { className: styles.paper },
};

function renderUserLabel(user: CmsUserSummary): string {
    return `${user.displayName} (${user.email})`;
}

function renderLastLogin(lastLogin: string | null): string {
    if (!lastLogin) return 'never';
    return new Date(lastLogin).toLocaleString();
}

function isSamePerson(a: string | undefined, b: string): boolean {
    return !!a && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function UserManagement() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<CmsUserSummary | null>(
        null,
    );

    const { blocks, isLoading: isLoadingCatalog } = useUserRoleCatalog();
    const { users: suggestions, isLoading: isSearching } =
        useCmsUserSearch(searchTerm);
    const { user: storedUser } = useCmsUser(selectedUser?.id ?? null);
    const { data: currentUser } = useUserProfile();

    const user = storedUser ?? selectedUser;

    if (isLoadingCatalog) {
        return <Loading text="Loading roles, hang on..." />;
    }

    return (
        <>
            <Section title="User management">
                <div className={styles.searchRow}>
                    <span className={styles.searchLabel}>{SEARCH_LABEL}</span>
                    <Autocomplete<CmsUserSummary, false, true>
                        className={styles.field}
                        options={suggestions}
                        getOptionLabel={renderUserLabel}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        filterOptions={(options) => options}
                        loading={isSearching}
                        noOptionsText={
                            searchTerm
                                ? 'No CMS user matched that email or name.'
                                : 'Start typing an email or a name.'
                        }
                        value={user ?? undefined}
                        onChange={(_event, selected) =>
                            setSelectedUser(selected)
                        }
                        slotProps={AUTOCOMPLETE_SLOT_PROPS}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                slotProps={{
                                    ...params.slotProps,
                                    htmlInput: {
                                        ...params.slotProps?.htmlInput,
                                        'aria-label': SEARCH_LABEL,
                                    },
                                }}
                                placeholder="jane.doe@tocaboca.com"
                                onChange={(event) =>
                                    setSearchTerm(event.target.value)
                                }
                            />
                        )}
                    />
                </div>

                {user ? (
                    <div className={styles.summary}>
                        {user.avatar ? (
                            <img
                                className={styles.avatar}
                                src={user.avatar}
                                alt=""
                            />
                        ) : null}
                        <div>
                            <h2>{user.displayName}</h2>
                            <div className={styles.meta}>
                                {user.email} · last sign in:{' '}
                                {renderLastLogin(user.lastLogin)}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className={styles.hint}>
                        Search for somebody who has signed into the CMS at least
                        once, then grant or revoke their roles. Changes only
                        take effect the next time that user signs in.
                    </p>
                )}
            </Section>

            {storedUser ? (
                <RoleEditor
                    key={storedUser.id}
                    user={storedUser}
                    blocks={blocks}
                    isEditingSelf={isSamePerson(
                        currentUser?.email,
                        storedUser.email,
                    )}
                />
            ) : null}
        </>
    );
}
